import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, InputGroup, Badge, Spinner, Button } from 'react-bootstrap';

// Standard mock blogs fallback database matching et_blogs schema
const MOCK_BLOGS = [
  {
    blog_id: 1,
    blog_title: "Nurturing Your Emotional Health: Simple Daily Practices",
    blog_author: "Sarah Jenkins",
    blog_slug: "nurturing-your-emotional-health-simple-daily-practices",
    blog_short_description: "Discover simple yet powerful habits to improve your emotional well-being and build resilience in your daily life.",
    blog_description: "<p>Our emotional health is just as important as our physical health, yet it is often neglected. Managing emotions effectively is key to a happy and healthy life. Taking time each day for self-reflection can dramatically alter how we process stress.</p><p>Here are a few quick tips to start with:</p><ul><li><b>Mindful Breathing:</b> Dedicate five minutes each morning to quiet, conscious breathing.</li><li><b>Gratitude Journaling:</b> Write down three things you are thankful for every evening.</li><li><b>Anonymous Expression:</b> Sharing your thoughts in a safe space like Emotional Touch helps release mental burden.</li></ul>",
    blog_category: "Mental Wellness",
    blog_image: "https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?auto=format&fit=crop&q=80&w=800",
    blog_created_on: "2026-08-10T10:00:00.000Z",
    blog_tag: "Wellness, Mindfulness, Selfcare"
  },
  {
    blog_id: 2,
    blog_title: "How to Find a Judgment-Free Zone for Mental Support",
    blog_author: "David Miller",
    blog_slug: "find-judgment-free-zone-mental-support",
    blog_short_description: "Venting and sharing your struggles without fear of judgment is crucial. Here is how finding a supportive community helps healing.",
    blog_description: "<p>Venting is a natural human need. However, finding a safe space to do so is increasingly rare. A judgment-free zone is a sanctuary where you can share your raw thoughts and feelings without the fear of criticism or social repercussions.</p><p>Building trust requires a community built on empathy rather than judgment. Online support groups, dedicated counselling, and anonymous listeners are fantastic avenues for finding this relief. Opening up is the first step toward self-discovery and recovery.</p>",
    blog_category: "Support",
    blog_image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&q=80&w=800",
    blog_created_on: "2026-08-08T12:00:00.000Z",
    blog_tag: "Support, Therapy, Healing"
  },
  {
    blog_id: 3,
    blog_title: "Rebuilding Hope and Happiness After a Tough Breakup",
    blog_author: "Elena Rostova",
    blog_slug: "rebuilding-hope-and-happiness-after-breakup",
    blog_short_description: "Dealing with heartbreak can feel overwhelming. Learn gentle ways to heal your heart and step back into joy and emotional wellness.",
    blog_description: "<p>Heartbreak is one of the most challenging emotional hurdles. But with patience, mindfulness, and the right support system, it is possible to find joy again. Healing is not linear, and it is important to treat yourself with compassion during these transition times.</p><p>Allow yourself to feel the sadness, but remember that a single ending does not define your entire story. Surround yourself with caring listeners, pursue new hobbies, and rebuild your independence step-by-step.</p>",
    blog_category: "Relationships",
    blog_image: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?auto=format&fit=crop&q=80&w=800",
    blog_created_on: "2026-08-05T09:00:00.000Z",
    blog_tag: "Breakup, Relationships, Growth"
  }
];

function BlogsController(props) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories, setCategories] = useState(["All"]);

  useEffect(() => {
    // SEO Enhancement
    document.title = "Blogs - Emotional Touch";

    const fetchBlogs = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_BASE_API_URL + '/get-blogs-data');
        const data = await response.json();
        
        if (data.status === 'success' && data.data && data.data.blogs_list) {
          const list = data.data.blogs_list;
          setBlogs(list);
          extractCategories(list);
        } else {
          // Fallback to mock data if API is successful but empty/invalid
          setBlogs(MOCK_BLOGS);
          extractCategories(MOCK_BLOGS);
        }
      } catch (error) {
        console.warn('API error fetching blogs, using premium fallback mock data:', error);
        setBlogs(MOCK_BLOGS);
        extractCategories(MOCK_BLOGS);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const extractCategories = (blogList) => {
    const cats = ["All"];
    blogList.forEach(blog => {
      if (blog.blog_category && !cats.includes(blog.blog_category)) {
        cats.push(blog.blog_category);
      }
    });
    setCategories(cats);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const selectCategory = (category) => {
    setSelectedCategory(category);
  };

  // Filter logic
  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = 
      blog.blog_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.blog_short_description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (blog.blog_tag && blog.blog_tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
    const matchesCategory = 
      selectedCategory === "All" || 
      blog.blog_category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <main>
      <section className="banner_section">
        <Container>
          <div className="page-title">
            <h2>Our Blogs</h2>
            <p className="banner_des">Read our latest articles on emotional intelligence, mental support, wellness, and selfcare.</p>
          </div>
        </Container>
      </section>

      <section className="blogs-list-section">
        <Container>
          {/* Search and Filters */}
          <div className="blogs-filter-bar">
            <Row className="justify-content-center">
              <Col lg={6} md={8} sm={12}>
                <InputGroup className="search-input-group">
                  <Form.Control
                    type="text"
                    placeholder="Search blogs by title, keywords or tags..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    id="blog-search-input"
                  />
                  <Button className="btn">
                    <i className="fas fa-search"></i>
                  </Button>
                </InputGroup>
              </Col>
            </Row>

            {/* Category pills */}
            <div className="category-pills">
              {categories.map((cat, idx) => (
                <div
                  key={idx}
                  className={`category-pill ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => selectCategory(cat)}
                  id={`cat-pill-${idx}`}
                >
                  {cat}
                </div>
              ))}
            </div>
          </div>

          {/* Loading state */}
          {loading ? (
            <div className="blogs-loading-state">
              <Spinner animation="border" variant="danger" className="mb-3" />
              <p>Loading blogs, please wait...</p>
            </div>
          ) : (
            <>
              {filteredBlogs.length === 0 ? (
                <div className="text-center py-5 text-secondary">
                  <i className="far fa-folder-open fa-3x mb-3"></i>
                  <p className="fs-5">No blogs found matching your search criteria.</p>
                </div>
              ) : (
                <Row>
                  {filteredBlogs.map((blog) => (
                    <Col lg={4} md={6} sm={12} key={blog.blog_id} className="mb-4">
                      <Card className="blog-card" id={`blog-card-${blog.blog_id}`}>
                        <div className="blog-card-img-wrapper">
                          <img
                            src={blog.blog_image ? blog.blog_image.replace('https:/', 'https://') : 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=800'}
                            alt={blog.blog_title}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=800';
                            }}
                          />
                        </div>
                        <Card.Body className="blog-card-body">
                          <div className="blog-card-meta">
                            {blog.blog_category && (
                              <span>
                                <Badge bg="danger" className="me-2">{blog.blog_category}</Badge>
                              </span>
                            )}
                            <span>
                              <i className="far fa-calendar-alt"></i>
                              {formatDate(blog.blog_created_on)}
                            </span>
                          </div>
                          <Link to={`/blog/${blog.blog_slug}`} className="blog-card-title">
                            {blog.blog_title}
                          </Link>
                          <Card.Text className="blog-card-excerpt">
                            {blog.blog_short_description}
                          </Card.Text>
                          <div className="blog-card-footer">
                            <div className="blog-author-info">
                              <div className="blog-author-avatar">
                                {blog.blog_author ? blog.blog_author.charAt(0).toUpperCase() : 'A'}
                              </div>
                              <span className="blog-author-name">By {blog.blog_author || "Admin"}</span>
                            </div>
                            <Link to={`/blog/${blog.blog_slug}`} className="blog-read-more">
                              Read More <i className="fas fa-arrow-right"></i>
                            </Link>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </>
          )}
        </Container>
      </section>
    </main>
  );
}

export default BlogsController;
